
import { computed, defineComponent, onMounted, reactive, ref, inject } from 'vue';
import { ElMessage, ElMessageBox, ElForm } from 'element-plus'
import { forEach, isEmpty, isString } from 'lodash-es'
import dayjs from 'dayjs'
import { nanoid } from 'nanoid'
import { useStore } from '../store/index'
import testData from '../testData'
import UserSelector from '../components/UserSelector.vue'
import useFilterSearch from '../hooks/useFilterSearch'
type FormInstance = InstanceType<typeof ElForm>
export default defineComponent({
  name: 'Resume',
  components: {
    UserSelector
  },
  setup() {
    const baseAPIURL = inject('baseAPIURL')
    const store = useStore()
    const dialogVisible = ref(false)
    const editMode = ref('')
    const ruleFormRef = ref<FormInstance>()
    const count = computed(() => store.state.resume.count)
    const categories = computed(() => store.state.example.categories)
    const templates = computed(() => store.state.template.data)
    const isLoading = computed(() => store.getters.isLoading)
    const categoriesWithEmpty = computed(() => {
      const copiedCategories = categories.value.map((category) => {
        const { _id, name } = category
        return {
          _id,
          name
        }
      })
      copiedCategories.unshift({ _id: '', name: '空'})
      return copiedCategories
    })
    const resumes = computed(() => store.state.resume.data.map(resume => {
      resume.createdAt = dayjs(resume.createdAt).format('YYYY-MM-DD HH:mm') 
      resume.updatedAt = dayjs(resume.updatedAt).format('YYYY-MM-DD HH:mm')
      return resume
    }))
    const { 
      currentPage,
      PAGE_SIZE,
      searchForm,
      searchTriggered,
      checksRef,
      performGetList,
      resetValues,
      onSort
    } = useFilterSearch('getResumesAdmin', { isExamples: false, 'config.isPublic': false, 'config.addToExamples': false })

    const searchRules = reactive({
      type: [
        {
          required: true,
          message: '请输入搜索类型',
          trigger: 'change',
        },
      ],
      value: [
        {
          required: true,
          message: '请输入搜索值',
          trigger: 'blur',
        },        
      ]
    })
    const currentThemes = computed(() => {
      const currentTemplate = templates.value.find((template) => template.componentName === formData.value.config.templateName)
      return currentTemplate ? currentTemplate.themes.map(theme => theme.name) : []
    })
    const onJsonChange = (value) => {
      formData.value.data = value
    }

    const formData = ref<any>({
      name: '',
      desc: '',
      coverImg: '',
      userId: '',
      uuid: '',
      data: {},
      config:  { templateName: 'TemplateModern'},
      isExamples: false,
      category: ''
    })
    const triggerEdit = (index: number) => {
      dialogVisible.value = true
      const { _id, config, data, userId, uuid, name, desc, isExamples, category, coverImg } = resumes.value[index]
      editMode.value = _id
      console.log(userId)
      formData.value = {
        name,
        desc : desc || '',
        uuid,
        userId: userId._id,
        config,
        data: data as any,
        isExamples: isExamples || false,
        category: category || '',
        coverImg: coverImg || ''
      }
      // schema.properties.coverImage.default = coverImage
    }
    const schema = reactive({
      type: 'object',
      required: [
        'name',
        'uuid',
      ],
      properties: {
        name: {
          type: 'string',
          title: '简历名称',
          "ui:width": "33%",
        },
        desc: {
          type: 'string',
          title: '简历介绍',
          "ui:width": "33%",
        },
        uuid: {
          type: 'string',
          title: '简历短ID',
          "ui:width": "33%",
        },
        category: {
          title: "分类名称",
          type: "string",
          "enum": computed(() => categoriesWithEmpty.value.map(template => template._id)),
          "enumNames": computed(() => categoriesWithEmpty.value.map(template => template.name)),
          "ui:width": "33%",
        },
        isExamples: {
          type: 'boolean',
          title: '是否添加到实例',
          "ui:width": "33%",
        },
        config: {
          "type": "object",
          "name": "配置选项",
          "title": "配置选项",
          "properties": {
            templateName: {
              title: "模版名称",
              type: "string",
              "enum": computed(() => templates.value.map(template => template.componentName)),
              "enumNames": computed(() => templates.value.map(template => template.name)),
              "ui:width": "33%",
            },
            templateTheme: {
              title: "模版主题",
              type: "string",
              "enum": currentThemes,
              "enumNames": currentThemes,
              "ui:width": "33%",
            },
            "isPublic": {
              "title": "是否公开",
              "type": "boolean",
              "ui:width": "33%",
            },
            "emailIsHidden": {
              "title": "隐藏Email",
              "type": "boolean",
              "ui:width": "33%",
            },
            "cellphoneIsHidden": {
              "title": "隐藏手机号码",
              "type": "boolean",
              "ui:width": "33%",
            },
            "removeBranding": {
              "title": "隐藏Logo",
              "type": "boolean",
              "ui:width": "33%",
            },
            "addToExamples": {
              "title": "允许添加到实例",
              "type": "boolean",
              "ui:width": "33%",
            },
            "downloadable": {
              "title": "是否显示下载按钮",
              "type": "boolean",
              "ui:width": "33%",
            },
            "showShareBtn": {
              "title": "是否显示分享按钮",
              "type": "boolean",
              "ui:width": "33%",
            }
          }
        },
        coverImg: {
          "anyOfSelect": {
              "ui:widget": "RadioWidget",
              "ui:title": "简历图片",
              "ui:options": {}
          },
          anyOf: [
            {
              title: "上传图片",
              type: "string",
              default: "",
              "ui:action": `${baseAPIURL}/api/upload`,
              "ui:with-credentials": true,
              "ui:list-type": "picture",
              "ui:widget": "UploadWidget",
              "ui:btnText": "上传头图"
            }, 
            {
              title: "直接填写URL",
              type: "string",
            }
          ]
        }
      }
    })
    
    const onCreateNew = () => {
      const uuid = nanoid(6)
      formData.value = {
        name: 'test name',
        desc: '',
        coverImg: '',
        userId: '',
        uuid,
        data: testData,
        config:  {
          templateName: 'TemplateModern',
          templateTheme: 'emerald',
          isPublic: false,
          customURL: uuid,
          emailIsHidden: false,
          cellphoneIsHidden: false,
          removeBranding: false,
          addToExamples: false,
          downloadable: false,
          showShareBtn: false
        },
        isExamples: false,
        category: ''
      }
      dialogVisible.value = true
      editMode.value = ''  
    }
    const onSearch = () => {
      if (!ruleFormRef.value) return
      ruleFormRef.value.validate(async (valid) => {
        if (valid || (searchForm.dateRange && !valid)) {
          if (ruleFormRef.value) {
            ruleFormRef.value.clearValidate('type')
            ruleFormRef.value.clearValidate('value')
          }
          searchTriggered.value = true
          currentPage.value = 1
          await performGetList()
        } else {
          return false
        }
      })
    }
    const resetSearch = () => {
      if (!ruleFormRef.value) return
      searchForm.dateRange = ''
      ruleFormRef.value.resetFields()
      resetValues()
    }
    const onResearch = async () => {
      // reset search
      resetSearch()
      await performGetList()
    }
    const toggleDisabled = async (id:string, disabled: boolean) => {
      await store.dispatch('updateResume', { urlParams: { id }, data: { disabled } })
      ElMessage({
        type: 'success',
        message: '更新成功',
        duration: 1000
      })
    }
    const onCancel = () => {
      dialogVisible.value = false
    }
    const formatData = () => {
      const data: Record<string, any> = {}
      forEach(formData.value, (value, key) => {
        if (!(isString(value) && isEmpty(value))) {
            data[key] = value
        }
      })
      return data
    }
    const onSubmit = async () => {
      const finalData = formatData()
      // RQYF-68 需要手动的根据外层 uuid 的值，在赋值给内部 config 一次
      if (finalData.config) {
        finalData.config.customURL = finalData.uuid
      }
      if (editMode.value === '') {
        console.log('create', finalData)
        console.log('create', finalData.data)
        await store.dispatch('createResumeAdmin', { data: finalData })
        ElMessage({
          type: 'success',
          message: '创建成功',
          duration: 1000
        })
        dialogVisible.value = false
      } else {
        await store.dispatch('updateResume', { urlParams: { id: editMode.value }, data: finalData })
        // await store.dispatch('getResumesAdmin', { searchParams: { currentPage: 1, pageSize: PAGE_SIZE }})
        ElMessage({
          type: 'success',
          message: '更新成功',
          duration: 1000
        })
        dialogVisible.value = false
      }
    }
    const onUpdateSort = async (id: string, value: string) => {
      console.log(id, value)
      await store.dispatch('updateResume', { urlParams: { id }, data: { sort: parseInt(value) } })
      ElMessage({
        type: 'success',
        message: '更新成功',
        duration: 1000
      })
    }
    const onPageChange = async () => {
      console.log(currentPage.value)
      await performGetList()
    }
    const onDelete = (id: string) => {
      ElMessageBox.confirm('是否要永久删除该简历？',  '删除简历', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await store.dispatch('deleteResume', { urlParams: { id }})
        ElMessage({
          type: 'success',
          message: '删除成功',
          duration: 1000
        })
      }).catch((e) => {
        throw e
      })
    }
    onMounted(async () => {
      // await store.dispatch('getUsers', { searchParams: { currentPage: 1, pageSize: PAGE_SIZE }})
      await Promise.all([
        store.dispatch('getResumesAdmin', { searchParams: { currentPage: 1, pageSize: PAGE_SIZE }}), 
        store.dispatch('getTemplates'), 
        store.dispatch('getCategories')]
      )
    })
    return {
      resumes,
      schema,
      formData,
      triggerEdit,
      dialogVisible,
      onCancel,
      onSubmit,
      onCreateNew,
      editMode,
      onDelete,
      toggleDisabled,
      count,
      PAGE_SIZE,
      onPageChange,
      searchForm,
      searchRules,
      ruleFormRef,
      onSearch,
      onResearch,
      searchTriggered,
      currentPage,
      onJsonChange,
      onSort,
      checksRef,
      isLoading,
      onUpdateSort,
      dayjs
    }
  }
});
